import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useAdminUsers, useRolesCatalog, useSetUserRoles } from "@/hooks/useAdminUsers";
import type { AdminUserRow, RoleCatalogGroup } from "@/api/adminCenter";

/**
 * D-044 S2 — #4 Roles & access matrix (VISUAL v1.1).
 * (A) Каталог read-only (collapsible; MP3 Адмін окремо; MP6 «N носіїв»→фільтр;
 * system 🔒; deprecated ПОВНІСТЮ приховані — backend їх не віддає).
 * (B) Матриця users×осі: tier-select + confirm (AC-ADM-031) · functional +
 * MP8-наслідки + MP4 mismatch (BACKEND-calc) · capability + MP5 popover.
 * 0 scope-editor (AC-ADM-032). Мутації → setRoles (S1 canonical шлях, P8-audit,
 * resolver негайно — MP9/AC-ADM-033).
 */

export function AdminRolesPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;

  const usersQ = useAdminUsers(canView);
  const catalogQ = useRolesCatalog(canView);
  const rolesM = useSetUserRoles();

  const [catalogOpen, setCatalogOpen] = useState(true);
  const [holderFilter, setHolderFilter] = useState<{ label: string; ids: string[] } | null>(null);
  const [confirm, setConfirm] = useState<{
    user: AdminUserRow;
    oldLabel: string;
    newTier: string;
    newLabel: string;
  } | null>(null);
  const [fnNote, setFnNote] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  const matrixUsers = useMemo(() => {
    const users = (usersQ.data?.users ?? []).filter((u) => u.isActive);
    if (!holderFilter) return users;
    return users.filter((u) => holderFilter.ids.includes(u.id));
  }, [usersQ.data, holderFilter]);

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const options = usersQ.data?.options;

  const applyRoles = (user: AdminUserRow, next: { tier?: string | null; functional?: string | null; capabilities?: string[] }) => {
    const cfg = {
      tier:
        next.tier !== undefined
          ? next.tier
          : user.tier === "admin_data_all"
            ? "admin"
            : user.tier,
      functional:
        next.functional !== undefined
          ? next.functional
          : user.functional.length
            ? user.functional[0].key
            : null,
      capabilities:
        next.capabilities !== undefined
          ? next.capabilities
          : user.capabilities.map((c) => c.key),
    };
    rolesM.mutate(
      { userId: user.id, cfg },
      {
        onSuccess: () => showToast("ok", `Ролі ${user.name} оновлено · аудит · resolver синхронізовано`),
        onError: (e) => showToast("err", e instanceof Error ? e.message : "Помилка"),
      },
    );
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ShieldCheck className="h-5 w-5" /> Ролі та доступ
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Каталог ролей (read-only) і матриця призначень — лише з locked-шаблонів.
          </p>
        </div>

        {/* (A) Каталог */}
        <div className="rounded-card border bg-card">
          <button
            type="button"
            onClick={() => setCatalogOpen(!catalogOpen)}
            aria-expanded={catalogOpen}
            className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium"
          >
            {catalogOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Каталог ролей
          </button>
          {catalogOpen && (
            <div className="border-t px-4 pb-3">
              {catalogQ.isLoading && <Skeleton className="my-3 h-24 w-full" />}
              {catalogQ.data?.groups.map((g) => (
                <CatalogGroup
                  key={g.key}
                  group={g}
                  onHoldersClick={(label, ids) => setHolderFilter({ label, ids })}
                />
              ))}
              {catalogQ.data && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-muted px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {/* AC-ADM-032: 0 scope-editor, текст замість dead-кнопки */}
                  {catalogQ.data.scopeLockedNote}
                </div>
              )}
            </div>
          )}
        </div>

        {/* (B) Матриця */}
        <div className="rounded-card border bg-card">
          <div className="flex items-center gap-2 px-4 py-3">
            <span className="text-sm font-medium">Матриця призначень</span>
            {holderFilter && (
              <button
                type="button"
                onClick={() => setHolderFilter(null)}
                className="inline-flex items-center gap-1 rounded-badge bg-info px-2 py-0.5 text-xs text-info-foreground"
              >
                фільтр: {holderFilter.label} ({holderFilter.ids.length}) <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {confirm && (
            <div className="mx-4 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warning px-3 py-2.5 text-sm text-warning-foreground">
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Замінити Рівень {confirm.user.name}: {confirm.oldLabel} → {confirm.newLabel}? Вплине
                на видимість (1 tier/користувач).
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  disabled={rolesM.isPending}
                  onClick={() => {
                    applyRoles(confirm.user, { tier: confirm.newTier });
                    setConfirm(null);
                  }}
                >
                  {rolesM.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  Підтвердити
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
                  Скасувати
                </Button>
              </span>
            </div>
          )}
          {fnNote && (
            <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg bg-info px-3 py-2 text-xs text-info-foreground">
              <Check className="h-3.5 w-3.5" /> {fnNote}
            </div>
          )}

          {usersQ.isLoading && (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {usersQ.data && options && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Користувач</th>
                  <th className="px-2 py-2 font-medium">Рівень доступу</th>
                  <th className="px-2 py-2 font-medium">Функція / Відділ</th>
                  <th className="px-4 py-2 font-medium">Права</th>
                </tr>
              </thead>
              <tbody>
                {matrixUsers.map((u) => (
                  <MatrixRow
                    key={u.id}
                    user={u}
                    options={options}
                    busy={rolesM.isPending}
                    onTierChange={(newTier, newLabel) =>
                      setConfirm({
                        user: u,
                        oldLabel: u.tierLabel ?? "—",
                        newTier,
                        newLabel,
                      })
                    }
                    onFunctionalChange={(fnKey) => {
                      applyRoles(u, { functional: fnKey });
                      // MP8: наслідки
                      setFnNote(
                        fnKey
                          ? `Функція «${options.functional.find((o) => o.key === fnKey)?.label ?? fnKey}»: доступ до робочих черг/bounded context відділу (inbox — після responsible у #6).`
                          : "Функцію знято — department-черги недоступні.",
                      );
                      window.setTimeout(() => setFnNote(null), 5000);
                    }}
                    onCapToggle={(capKey, on) => {
                      const current = u.capabilities.map((c) => c.key);
                      applyRoles(u, {
                        capabilities: on ? [...current, capKey] : current.filter((k) => k !== capKey),
                      });
                    }}
                  />
                ))}
                {matrixUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      Нікого не знайдено за фільтром.
                      <Button variant="outline" size="sm" className="ml-2" onClick={() => setHolderFilter(null)}>
                        Скинути фільтр
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok" ? "bg-success text-success-foreground" : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Catalog ────────────────────────────────────────────────────────────────

function CatalogGroup({
  group,
  onHoldersClick,
}: {
  group: RoleCatalogGroup;
  onHoldersClick: (label: string, ids: string[]) => void;
}) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {group.title}
      </div>
      {group.warning && (
        <div className="mb-1.5 flex items-center gap-1.5 rounded bg-warning px-2 py-1 text-xs text-warning-foreground">
          <AlertTriangle className="h-3 w-3" /> {group.warning}
        </div>
      )}
      {group.rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between gap-2 border-b py-1.5 last:border-0">
          <span className="text-sm">
            {r.label} <span className="text-xs text-muted-foreground">· {r.hint}</span>
          </span>
          {group.readOnly ? (
            <span className="inline-flex items-center gap-1 rounded-badge bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" /> read-only
            </span>
          ) : r.holdersCount > 0 ? (
            // MP6: клік → фільтр матриці
            <button
              type="button"
              title="Клік → фільтр матриці на цих користувачів"
              onClick={() => onHoldersClick(r.label, r.holders.map((h) => h.id))}
              className="rounded-badge bg-info px-2 py-0.5 text-xs tabular-nums text-info-foreground hover:opacity-80"
            >
              {r.holdersCount} носі{r.holdersCount === 1 ? "й" : r.holdersCount < 5 ? "ї" : "їв"}
            </button>
          ) : (
            <span className="rounded-badge bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
              0 носіїв
            </span>
          )}
        </div>
      ))}
      {group.note && <p className="mt-1 text-xs text-muted-foreground">{group.note}</p>}
    </div>
  );
}

// ─── Matrix row ─────────────────────────────────────────────────────────────

const TIER_OPTIONS = [
  { key: "manager", label: "Менеджер" },
  { key: "supervisor", label: "Керівник" },
  { key: "director", label: "Директор" },
];

function MatrixRow({
  user,
  options,
  busy,
  onTierChange,
  onFunctionalChange,
  onCapToggle,
}: {
  user: AdminUserRow;
  options: NonNullable<ReturnType<typeof useAdminUsers>["data"]>["options"];
  busy: boolean;
  onTierChange: (tierKey: string, label: string) => void;
  onFunctionalChange: (fnKey: string | null) => void;
  onCapToggle: (capKey: string, on: boolean) => void;
}) {
  const [capPopover, setCapPopover] = useState<string | null>(null);
  const isAdmin = user.type === "admin";
  const fnCurrent = user.functional.length ? user.functional[0].key : "";

  return (
    <tr className="border-t">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-info text-[11px] text-info-foreground">
            {user.name
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() ?? "")
              .join("")}
          </span>
          {user.name}
        </div>
      </td>
      <td className="px-2 py-2.5">
        {isAdmin ? (
          // MP3: Адмін ≠ sales-tier — type-derived, тут locked
          <span
            className="inline-flex items-center gap-1 rounded-badge bg-info px-2 py-0.5 text-xs text-info-foreground"
            title="Адмін = системне адміністрування (type), не sales-tier"
          >
            <Lock className="h-3 w-3" /> Адмін
          </span>
        ) : (
          <select
            aria-label={`Рівень доступу ${user.name}`}
            disabled={busy}
            value={user.tier ?? ""}
            onChange={(e) => {
              const t = TIER_OPTIONS.find((o) => o.key === e.target.value);
              if (t && t.key !== user.tier) onTierChange(t.key, t.label);
              e.target.value = user.tier ?? ""; // revert до confirm
            }}
            className="h-8 rounded-lg border bg-card px-1.5 text-sm"
          >
            {user.tier === null && <option value="">— без ролі —</option>}
            {TIER_OPTIONS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="px-2 py-2.5">
        <select
          aria-label={`Функція ${user.name}`}
          disabled={busy}
          value={fnCurrent}
          onChange={(e) => onFunctionalChange(e.target.value || null)}
          className="h-8 rounded-lg border bg-card px-1.5 text-sm"
        >
          <option value="">—</option>
          {options.functional.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        {/* MP4: BACKEND-calc mismatch */}
        {user.functionalMismatch && (
          <div className="mt-1">
            <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-[10px] text-warning-foreground">
              <AlertTriangle className="h-3 w-3" /> роль ≠ відділ
            </span>
          </div>
        )}
      </td>
      <td className="relative px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          {options.capabilities.map((c) => {
            const on = user.capabilities.some((uc) => uc.key === c.key);
            return (
              <label key={c.key} className="flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busy}
                  onChange={() => onCapToggle(c.key, !on)}
                  className="h-3.5 w-3.5"
                />
                {c.label}
                {/* MP5: явний popover */}
                <button
                  type="button"
                  aria-label={`Що дає ${c.label}`}
                  onClick={() => setCapPopover(capPopover === c.key ? null : c.key)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </label>
            );
          })}
        </div>
        {capPopover && (
          <div className="absolute right-2 top-full z-10 mt-1 w-64 rounded-lg border bg-card p-3 text-xs leading-relaxed shadow-lg">
            <b>{options.capabilities.find((c) => c.key === capPopover)?.label}:</b> погоджує{" "}
            {capPopover === "ContractSalesApprover" ? "комерційні умови" : "юридичні умови"} договору.{" "}
            <b>Не</b> дає доступу до клієнтів і <b>не</b> є read:all.
          </div>
        )}
      </td>
    </tr>
  );
}
